import { usePageModels } from '@/context/PageModels';
import { datalayerPush } from '@/features/shared/HOCs/withGTM';
import {
  MuiFormControlLabel,
  MuiFormGroup,
  MuiInputAdornment,
  MuiTextField,
} from '@/features/shared/material-ui';
import PartnerOnly from '@/features/shared/PartnerOnly';
import StyledCheckbox from '@/features/shared/StyledCheckbox';
import { usePartnerOnly } from '@/hooks/usePartnerOnly';
import { Theme } from '@/utils/constants';
import {
  reduceCheckboxDefault,
  reducePublicationDateCheckbox,
} from '@/utils/reducers/reduceFilter';
import { useMediaQuery } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Search as SearchIcon } from '@material-ui/icons';
import clsx from 'clsx';
import { intersection } from 'lodash';
import matchSorter from 'match-sorter';
import React, { ChangeEvent, memo, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import ElasticComponentContext from '../ElasticComponentContext';
import useStyles from './FacetFilters.styles';

const useSearchBarStyles = makeStyles(
  (muiTheme) => ({
    root: {
      background: '#fff',
      border: 'none',
      borderRadius: '1.5em',
      overflow: 'hidden',

      '& .MuiInputBase-input': {
        padding: muiTheme.spacing(1, 1, 1, 0),
      },
    },
  }),
  { name: 'GenElasticFacetFiltersTextField' },
);

interface FacetFilterProps {
  theme?: ThemeType;
  facet: string;
  options: { value: string; count?: number | string; selected?: boolean }[];
  onChange?: any;
  renderLabel?: any;
  categoryLabel?: string;
  labels?: any;
  config?: any;
  hasSearch?: boolean;
  hasScroll?: boolean;
  CRMAuthorized?: boolean;
}

const defaultRenderLabel = ({
  item,
}: {
  item: { value: string; count: number | string; label?: string };
}) => `${item?.label || item.value} ${item.count ? `(${item.count})` : ''}`;

const isIncluded = ({ filters, value }: any) =>
  Array.isArray(value) ? !!intersection(filters, value).length : filters.includes(value);

const listReducerStrategy = new Map<string, any>([
  ['publication_date', reducePublicationDateCheckbox],
  ['release_date', reducePublicationDateCheckbox],
]);

const FacetFilters = ({
  theme = Theme.light,
  facet,
  options,
  renderLabel = defaultRenderLabel,
  onChange = () => null,
  categoryLabel = '',
  labels = {},
  config = {},
  hasSearch = false,
  hasScroll = true,
  CRMAuthorized = false,
}: FacetFilterProps) => {
  const searchBarClasses = useSearchBarStyles();
  const classes = useStyles({});
  const { queryParams, updateQueryParams } = useContext(ElasticComponentContext);
  const containerRef = useRef<HTMLDivElement>(null);
  const [filters, setFilters] = useState<any[]>([]);
  const [filteredFilters, setFilteredFilters] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const { esLabels } = usePageModels();

  const { isPartnerOnlyFilter } = usePartnerOnly();

  const handleChange = (value: string | string[]) => {
    const allFilters = queryParams.filters[facet] || [];
    let args: any[] = [];

    if (isIncluded({ filters: allFilters, value })) {
      args = allFilters.filter((tag: string) => {
        return Array.isArray(value) ? !value.includes(tag) : tag !== value;
      });
    } else {
      args = Array.isArray(value) ? [...allFilters, ...value] : [...allFilters, value];
    }

    const variantName = (
      containerRef?.current?.closest('[data-datalayer-variant]') as HTMLDivElement
    )?.dataset?.datalayerVariant;

    datalayerPush({
      compRef: containerRef,
      args: {
        params: { filters: { ...queryParams.filters, [facet]: args }, variantName },
      },
      event: 'gtm:custom:filters',
    });

    updateQueryParams(facet, args, true);
    onChange();
  };

  const handleInput = (event: ChangeEvent<HTMLInputElement>) => {
    const text = event.target.value || '';
    setSearchTerm(text);

    if (!text.trim().length) {
      setFilteredFilters(filters.filter(({ value }) => value !== 'null'));
      return;
    }

    const filteredValues = matchSorter(filters, text.trim(), { keys: ['value'] });
    setFilteredFilters(filteredValues.filter(({ value }) => value !== 'null'));
  };

  useEffect(() => {
    const list = options.filter((item) => item.value.length);
    const listReduced = listReducerStrategy.has(facet)
      ? listReducerStrategy.get(facet)({ list })
      : reduceCheckboxDefault({ list, labels: esLabels });

    setFilters(listReduced);
    setFilteredFilters(listReduced.filter(({ value }: any) => value !== 'null'));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  // Search functionality is show if it's required and has a minimum quantity of records.
  const showSearch = useMemo(
    () => hasSearch && filters.length >= (config?.min_filters_search || 15),
    [config?.min_filters_search, filters.length, hasSearch],
  );

  if (!filters.length) {
    return (
      <div>
        {labels?.no_filters_display || 'No filters to display for'} {facet}
      </div>
    );
  }

  const renderFilteredFilters = () => (
    <>
      {!filteredFilters.length && (
        <div>
          {labels?.no_matches_found || 'No matches found for'} {searchTerm}
        </div>
      )}
      {filteredFilters.map((filter: any) => (
        <div key={filter.value} className="filter-checkbox">
          <MuiFormGroup>
            <MuiFormControlLabel
              control={
                <StyledCheckbox
                  checked={
                    queryParams.filters[facet]
                      ? isIncluded({ filters: queryParams.filters[facet], value: filter.value })
                      : false
                  }
                  onChange={() => handleChange(filter?.value)}
                  name={filter?.label || filter.value}
                  color="secondary"
                  theme={theme}
                  className={classes.checkbox}
                />
              }
              label={
                <>
                  {renderLabel({ facet, item: filter })}{' '}
                  {isPartnerOnlyFilter(facet, filter) && (
                    <PartnerOnly className={classes.partnerOnly} onlyIcon />
                  )}
                </>
              }
              className={clsx(classes.label, !filter.count && 'has-no-values')}
            />
          </MuiFormGroup>
        </div>
      ))}
    </>
  );

  const rootClassName = clsx(classes.root, `${theme}-theme`);

  return (
    <div ref={containerRef} className={rootClassName}>
      {showSearch && (
        <div className={classes.inputWrapper}>
          <MuiTextField
            fullWidth
            value={searchTerm}
            onChange={handleInput}
            InputProps={{
              placeholder: `${labels.search || 'Search'} ${categoryLabel}`,
              classes: searchBarClasses,
              startAdornment: (
                <MuiInputAdornment position="start" className={classes.icon}>
                  <SearchIcon className={classes.iconImage} />
                </MuiInputAdornment>
              ),
              disableUnderline: true,
            }}
          />
        </div>
      )}
      {hasScroll && (
        <Scrollbars
          style={{ width: 'calc(100% + 15px)' }}
          autoHeight
          autoHeightMax={hasScroll ? '50vh' : undefined}
          autoHeightMin={hasScroll && !isMobile && showSearch ? '50vh' : undefined}
        >
          {renderFilteredFilters()}
        </Scrollbars>
      )}
      {!hasScroll && renderFilteredFilters()}
    </div>
  );
};

export default memo(FacetFilters);
